import { ReelSymbol } from "@lib/titan2-slingo/dist/game/view/abstract/display/ReelSymbol";
import { TitanSpine } from "@lib/titan2/dist/game/control/TitanSpine";
import { SpineUtil } from "@lib/titan2/dist/util/view/SpineUtil";
import { Emitter, LinkedListContainer } from "@pixi/particle-emitter";
import { GameSpineUtil } from "../../../utils/GameSpineUtil";
import { GameSymbolUtil } from "../../../utils/GameSymbolUtil";
/**
 * Mostly overridden for using custom skins and animations.
 * Could be simplified in usage, but matches ReelSymbol API for consistency.
 */
export class GameReelSymbol extends ReelSymbol {
    constructor(landscapeProperties, portraitProperties, reelProperties) {
        super(landscapeProperties, portraitProperties, reelProperties);
        this.number = 0;
        this.strikePromise = Promise.resolve();
        this.scatterDelayPromise = Promise.resolve();
        this.emptySpine = TitanSpine.createSpine(this, landscapeProperties, portraitProperties);
        this.emptySpine.visible = false;
        this.addChildAt(this.emptySpine, 0);
        const emitterContainer = this.addChildAt(new LinkedListContainer(), 0);
        this.emitter = new Emitter(emitterContainer, this.reelProperties.particleEmitter);
        this.emitter.emit = false;
        this.run();
    }
    reset() {
        // super.reset();
        this.emitter.emit = false;
    }
    async fadeOut() {
        if (this.number) {
            await new Promise(resolve => SpineUtil.play(this.spine, "fade-out", resolve));
            this.emptySpine.visible = true;
        }
        else {
            this.emptySpine.visible = true;
            await gsap.to(this.spine, { alpha: 0, duration: 0.5 });
            this.spine.alpha = 1;
        }
        this.spine.visible = false;
    }
    showJoker() {
        this.number = 0;
        GameSymbolUtil.setJokerSkin(this.spine);
        this.spine.visible = true;
        this.emptySpine.visible = false;
    }
    showSuperJoker() {
        this.number = 0;
        GameSymbolUtil.setSuperJokerSkin(this.spine);
        this.spine.visible = true;
        this.emptySpine.visible = false;
    }
    showFreeSpin() {
        this.number = 0;
        GameSymbolUtil.setFreeSpinSkin(this.spine);
        this.spine.visible = true;
        this.emptySpine.visible = false;
    }
    showScatter() {
        this.number = 0;
        GameSymbolUtil.setScatterSkin(this.spine);
        this.spine.visible = true;
        this.emptySpine.visible = false;
    }
    showEmpty(column) {
        this.number = 0;
        GameSymbolUtil.setColumnSkin(this.emptySpine, column);
        GameSymbolUtil.setColumnSkin(this.spine, column);
        this.emptySpine.visible = true;
        this.spine.visible = false;
    }
    showNumber(number) {
        if (this.number !== number) {
            this.number = number;
            GameSymbolUtil.setNumberSkin(this.spine, number);
            this.spine.visible = true;
            this.emptySpine.visible = false;
        }
    }
    animateStrike() {
        SpineUtil.stop(this.spine);
        this.strikePromise = new Promise(resolve => SpineUtil.play(this.spine, "strike", resolve, null, 0));
        if (this.number > 0) {
            SpineUtil.play(this.spine, this.getHighlightAnimation(), null, null, 1);
        }
    }
    animateStop() {
        SpineUtil.stop(this.spine);
        SpineUtil.play(this.spine, "default");
    }
    animateActivate() {
        SpineUtil.stop(this.spine);
        SpineUtil.play(this.spine, "activate");
    }
    animateCollect() {
        SpineUtil.stop(this.spine);
        SpineUtil.play(this.spine, "collect");
    }
    strikeJoker() {
        this.showJoker();
        this.animateStrike();
    }
    stopJoker() {
        this.showJoker();
        this.animateStop();
        this.emitter.emit = false;
    }
    activateJoker() {
        this.showJoker();
        this.animateActivate();
        this.handleFadeInWildParticles();
    }
    collectJoker() {
        this.showJoker();
        this.animateCollect();
        this.handleFadeOutWildParticles();
    }
    strikeSuperJoker() {
        this.showSuperJoker();
        this.animateStrike();
    }
    stopSuperJoker() {
        this.showSuperJoker();
        this.animateStop();
        this.emitter.emit = false;
    }
    activateSuperJoker() {
        this.showSuperJoker();
        this.animateActivate();
        this.handleFadeInWildParticles();
    }
    collectSuperJoker() {
        this.showSuperJoker();
        this.animateCollect();
        this.handleFadeOutWildParticles();
    }
    strikeFreeSpin() {
        this.showFreeSpin();
        this.animateStrike();
    }
    stopFreeSpin() {
        this.showFreeSpin();
        this.animateStop();
    }
    collectFreeSpin() {
        this.showFreeSpin();
        this.animateCollect();
    }
    strikeNumber(number = this.number) {
        this.showNumber(number);
        this.animateStrike();
    }
    stopNumber(number = this.number) {
        this.showNumber(number);
        this.animateStop();
    }
    collectNumber(number = this.number) {
        this.showNumber(number);
        this.animateCollect();
    }
    strikeScatter() {
        this.showScatter();
        this.animateStrike();
    }
    stopScatter() {
        this.showScatter();
        SpineUtil.gotoAndStop(this.spine, 0, "default");
    }
    stopScatterWithHighlight() {
        this.showScatter();
        SpineUtil.gotoAndStop(this.spine, 0, "activate");
    }
    async suspenseScatter() {
        // don't interrupt strike animation
        await this.strikePromise;
        const playingAnimation = this.spine.getSpine().state.getCurrent(0);
        if (playingAnimation && playingAnimation.animation.name === "default") {
            return; // Suspense stop already called
        }
        else if (playingAnimation && playingAnimation.animation.name === "activate") {
            return; // Activate already called
        }
        this.activateScatter();
    }
    activateScatter() {
        this.showScatter();
        SpineUtil.gotoAndStop(this.spine, 0, "activate");
        GameSpineUtil.forceUpdate(this.spine);
        for (const spine of GameSpineUtil.orientations(this.spine)) {
            const glowSprite = GameSpineUtil.getSlot(spine, "symbol-scatter").getChildAt(0);
            gsap.fromTo(glowSprite, { alpha: 0 }, { alpha: 1, duration: 0.5 });
        }
        const timeForNextScatter = this.getScatterActivateTime();
        this.scatterDelayPromise = new Promise(resolve => this.callAfter(timeForNextScatter * 1000, () => {
            SpineUtil.playLoop(this.spine, "activate");
            resolve();
        }));
        GameReelSymbol.lastActivatedScatter = this;
    }
    getScatterActivateTime() {
        const lastActivatedScatter = GameReelSymbol.lastActivatedScatter;
        if (!lastActivatedScatter) {
            return 0;
        }
        const spine = lastActivatedScatter.spine.getSpine();
        const animation = spine.state.getCurrent(0);
        const totalAnimationTime = spine.spineData.findAnimation("activate").duration;
        const currentAnimationTime = animation.trackTime;
        const adjustedAnimationTime = (currentAnimationTime <= GameReelSymbol.TIME_DELAY_BETWEEN_SCATTERS) ? currentAnimationTime + totalAnimationTime : currentAnimationTime;
        const timeForNextScatter = totalAnimationTime - (adjustedAnimationTime - GameReelSymbol.TIME_DELAY_BETWEEN_SCATTERS) % totalAnimationTime;
        return timeForNextScatter;
    }
    collectScatter() {
        this.showScatter();
        this.animateCollect();
    }
    async awaitScatterSuspenseDelay() {
        return this.scatterDelayPromise;
    }
    async stopScatterSuspense() {
        await this.strikePromise;
        const playingAnimation = this.spine.getSpine().state.getCurrent(0);
        if (playingAnimation?.animation.name === "activate") {
            const animationDuration = playingAnimation.animation.duration;
            const animationTime = (playingAnimation.trackTime + animationDuration) % animationDuration;
            playingAnimation.trackTime = animationTime;
            playingAnimation.loop = false;
            await new Promise(resolve => this.callAfter((animationDuration - animationTime) * 1000, resolve));
        }
        GameReelSymbol.lastActivatedScatter = undefined;
    }
    async cancelScatterSuspense() {
        await this.strikePromise;
        this.stopScatter();
        GameReelSymbol.lastActivatedScatter = undefined;
    }
    getHighlightAnimation() {
        const column = GameSymbolUtil.numberToColumn(this.number);
        return `highlight-${column}`;
    }
    handleFadeInWildParticles() {
        this.emitter.emit = true;
    }
    async handleFadeOutWildParticles() {
        this.emitter.emit = false;
    }
}
GameReelSymbol.TIME_DELAY_BETWEEN_SCATTERS = 0.25;
//# sourceMappingURL=GameReelSymbol.js.map